/**
 * Swedish Bank Account Validation Utilities (Frontend)
 *
 * Swedish bank accounts consist of:
 * - Clearing number (4-5 digits): Identifies bank and branch
 * - Account number (variable length): The actual account number
 *
 * Two main account types:
 * - Type 1: Clearing number is PART of the 11-digit account number (newer standard)
 * - Type 2: Clearing number is SEPARATE from the account number (legacy)
 *
 * Based on official Bankgirot specification (2024-02-22)
 * Sources:
 * - https://www.bankgirot.se/globalassets/dokument/anvandarmanualer/bankernaskontonummeruppbyggnad_anvandarmanual_sv.pdf
 * - https://swantzter.se/deep-dives/kontonummer/
 */

/**
 * Bank clearing number ranges and their validation rules
 * Each entry represents a specific clearing number range with its validation rules
 */
const BANK_INFO = [
  // Sveriges Riksbank (Sweden's Central Bank)
  {
    ranges: [[1000, 1099]],
    bank: 'Sveriges Riksbank',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1',
    comment: 'Sveriges centralbank'
  },

  // Nordea
  {
    ranges: [[1100, 1199]],
    bank: 'Nordea',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1',
    comment: 'Personkonto'
  },
  {
    ranges: [[1200, 1399]],
    bank: 'Danske Bank',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[1400, 1499]],
    bank: 'Nordea',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[1500, 1599]],
    bank: 'Ålandsbanken',
    accountType: 2,
    accountLength: [7, 8],
    checkDigit: 'mod10'
  },
  {
    ranges: [[1600, 1699]],
    bank: 'Standish',
    accountType: 2,
    accountLength: 7,
    checkDigit: null
  },
  {
    ranges: [[1700, 1799]],
    bank: 'Ikano Bank',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10'
  },
  {
    ranges: [[1800, 1899]],
    bank: 'Calyon Bank',
    accountType: 2,
    accountLength: 7,
    checkDigit: null
  },
  {
    ranges: [[2000, 2099]],
    bank: 'Nordea',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[2300, 2310]],
    bank: 'Ålandsbanken',
    accountType: 2,
    accountLength: [7, 8],
    checkDigit: 'mod10'
  },
  {
    ranges: [[2400, 2499]],
    bank: 'Danske Bank',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[3000, 3299]],
    bank: 'Nordea',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[3300, 3300]],
    bank: 'Nordea',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10',
    comment: 'Personkonto (baserat på personnummer)'
  },
  {
    ranges: [[3301, 3399]],
    bank: 'Nordea',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[3400, 3409]],
    bank: 'Länsförsäkringar Bank',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[3410, 3781]],
    bank: 'Nordea',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[3782, 3782]],
    bank: 'Nordea',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10'
  },
  {
    ranges: [[3783, 3999]],
    bank: 'Nordea',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[4000, 4999]],
    bank: 'Nordea',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10'
  },

  // SEB
  {
    ranges: [[5000, 5999]],
    bank: 'SEB',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },

  // Handelsbanken
  {
    ranges: [[6000, 6999]],
    bank: 'Handelsbanken',
    accountType: 2,
    accountLength: 9,
    checkDigit: 'mod11-2',
    comment: 'Check digit beräknas endast på kontonumret'
  },

  // Swedbank (modern clearing numbers)
  {
    ranges: [[7000, 7999]],
    bank: 'Swedbank',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },

  // Swedbank och Sparbankerna (Type 2 with 5-digit clearing)
  {
    ranges: [[8000, 8999]],
    bank: 'Swedbank',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10',
    clearingLength: 5,
    clearingCheckDigit: true,
    comment: 'Clearingnummer har 5:e siffra (kontrollsiffra)'
  },

  // Länsförsäkringar Bank
  {
    ranges: [[9020, 9029]],
    bank: 'Länsförsäkringar Bank',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10'
  },
  {
    ranges: [[9040, 9049]],
    bank: 'Citibank',
    accountType: 2,
    accountLength: 10,
    checkDigit: null
  },
  {
    ranges: [[9060, 9069]],
    bank: 'Länsförsäkringar Bank',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[9070, 9079]],
    bank: 'JAK Medlemsbank',
    accountType: 2,
    accountLength: 10,
    checkDigit: null
  },

  // SEB
  {
    ranges: [[9120, 9124]],
    bank: 'SEB',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },
  {
    ranges: [[9130, 9149]],
    bank: 'SEB',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },

  // Skandiabanken
  {
    ranges: [[9150, 9169]],
    bank: 'Skandiabanken',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },

  // Danske Bank
  {
    ranges: [[9180, 9189]],
    bank: 'Danske Bank',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10'
  },

  // Marginalen Bank
  {
    ranges: [[9230, 9239]],
    bank: 'Marginalen Bank',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10'
  },

  // SBAB
  {
    ranges: [[9250, 9259]],
    bank: 'SBAB',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10'
  },

  // ICA Banken
  {
    ranges: [[9270, 9279]],
    bank: 'ICA Banken',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },

  // Resurs Bank
  {
    ranges: [[9280, 9289]],
    bank: 'Resurs Bank',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },

  // Swedbank
  {
    ranges: [[9300, 9349]],
    bank: 'Swedbank',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },

  // Nordea
  {
    ranges: [[9500, 9549]],
    bank: 'Nordea',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10'
  },

  // Avanza Bank
  {
    ranges: [[9550, 9569]],
    bank: 'Avanza Bank',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-1'
  },

  // Sparbanken Syd
  {
    ranges: [[9570, 9579]],
    bank: 'Sparbanken Syd',
    accountType: 1,
    accountLength: 7,
    checkDigit: 'mod11-2'
  },

  // Nordea
  {
    ranges: [[9960, 9969]],
    bank: 'Nordea',
    accountType: 2,
    accountLength: 10,
    checkDigit: 'mod10'
  }
];

/**
 * Normalize clearing number (remove spaces, dashes)
 */
export function normalizeClearingNumber(clearingNumber) {
  if (!clearingNumber) return '';
  return clearingNumber.replace(/[\s-]/g, '');
}

/**
 * Normalize account number (remove spaces, dashes)
 */
export function normalizeAccountNumber(accountNumber) {
  if (!accountNumber) return '';
  return accountNumber.replace(/[\s-]/g, '');
}

/**
 * Get bank information based on clearing number
 */
export function getBankInfo(clearingNumber) {
  const normalized = normalizeClearingNumber(clearingNumber);

  // For Swedbank 8xxx, we need to handle the 5th check digit
  let clearing = parseInt(normalized, 10);

  if (isNaN(clearing)) return null;

  // If clearing is 5 digits and starts with 8, use only first 4 digits for range lookup
  if (normalized.length === 5 && normalized[0] === '8') {
    clearing = parseInt(normalized.substring(0, 4), 10);
  }

  for (const info of BANK_INFO) {
    for (const [min, max] of info.ranges) {
      if (clearing >= min && clearing <= max) {
        return info;
      }
    }
  }

  return null;
}

/**
 * Modulo 10 check (Luhn algorithm)
 * Used for Type 2 accounts and Swedbank 8xxx clearing check digit
 *
 * Working right to left:
 * - Position 1 (rightmost) is the check digit
 * - EVEN positions (2, 4, 6, 8, ...) are doubled
 * - ODD positions (1, 3, 5, 7, ...) are not doubled
 */
function modulo10Check(number) {
  const digits = number.split('').map(Number);
  let sum = 0;

  // Process all digits except the last one (which is the check digit)
  for (let i = 0; i < digits.length - 1; i++) {
    let digit = digits[i];
    // Position from right (1-indexed, where check digit is position 1)
    // For a 10-digit number: positions are 10, 9, 8, 7, 6, 5, 4, 3, 2
    // Double EVEN positions: 10, 8, 6, 4, 2 (when (position) % 2 === 0)
    const positionFromRight = digits.length - i;
    if (positionFromRight % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === digits[digits.length - 1];
}

/**
 * Modulo 11 check - Type 1 (full clearing + account)
 * Used by most Type 1 accounts
 * Weights: [1, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1] (descending from position 2)
 */
function modulo11Type1Check(clearingNumber, accountNumber) {
  // For Type 1: use clearing digits 2-4 + 7-digit account number = 10 digits
  const clearingDigits = clearingNumber.substring(1, 4); // Digits 2-4 of clearing
  const fullNumber = clearingDigits + accountNumber;
  const digits = fullNumber.split('').map(Number);

  const weights = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
  let sum = 0;

  for (let i = 0; i < digits.length - 1; i++) {
    sum += digits[i] * weights[i];
  }

  const remainder = sum % 11;
  const checkDigit = remainder === 0 ? 0 : 11 - remainder;

  return checkDigit === digits[digits.length - 1];
}

/**
 * Modulo 11 check - Type 2 (account only, for Handelsbanken and similar)
 * Used by Handelsbanken and some other banks
 * Check digit calculated on account number only
 */
function modulo11Type2Check(accountNumber) {
  const digits = accountNumber.split('').map(Number);
  const weights = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 10, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;

  for (let i = 0; i < digits.length - 1; i++) {
    sum += digits[i] * weights[i];
  }

  const remainder = sum % 11;
  const checkDigit = remainder === 0 ? 0 : 11 - remainder;

  return checkDigit === digits[digits.length - 1];
}

/**
 * Validate Swedbank 8xxx clearing number with 5th digit check
 */
function validateSwedbank8xxxClearing(clearingNumber) {
  if (clearingNumber.length !== 5) {
    return { valid: true, message: '5:e siffran (kontrollsiffra) saknas men är valfri' };
  }

  // The 5th digit is a mod-10 check digit on the first 4 digits
  const firstFour = clearingNumber.substring(0, 4);
  const checkDigit = parseInt(clearingNumber[4], 10);

  // Calculate expected check digit using mod-10
  const digits = firstFour.split('').map(Number);
  let sum = 0;

  for (let i = 0; i < 4; i++) {
    let digit = digits[i];
    if (i % 2 === 1) { // Positions 2 and 4 (0-indexed 1 and 3)
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }

  const expectedCheckDigit = (10 - (sum % 10)) % 10;

  if (expectedCheckDigit !== checkDigit) {
    return {
      valid: false,
      message: '5:e siffran (kontrollsiffra) stämmer inte. Rätt siffra: ' + expectedCheckDigit
    };
  }

  return { valid: true, message: 'Clearingnummer OK (inkl. kontrollsiffra)' };
}

/**
 * Validate clearing number format
 */
export function validateClearingNumber(clearingNumber) {
  if (!clearingNumber) {
    return { valid: false, message: 'Clearingnummer är obligatoriskt' };
  }

  const normalized = normalizeClearingNumber(clearingNumber);

  // Must be 4 or 5 digits
  if (!/^\d{4,5}$/.test(normalized)) {
    return { valid: false, message: 'Clearingnummer måste vara 4-5 siffror' };
  }

  // Must be in valid range (1000-9999)
  const clearingInt = parseInt(normalized.substring(0, 4), 10);
  if (clearingInt < 1000 || clearingInt > 9999) {
    return { valid: false, message: 'Clearingnummer måste vara mellan 1000-9999' };
  }

  // Get bank info
  const bankInfo = getBankInfo(normalized);

  if (!bankInfo) {
    return {
      valid: true, // Accept but warn
      bank: 'Okänd bank',
      message: 'Clearingnummer accepterat (bank kunde ej identifieras)',
      warning: true
    };
  }

  // Special validation for Swedbank 8xxx with 5th digit
  if (bankInfo.clearingCheckDigit && normalized[0] === '8') {
    const clearingValidation = validateSwedbank8xxxClearing(normalized);
    if (!clearingValidation.valid) {
      return {
        valid: false,
        bank: bankInfo.bank,
        message: clearingValidation.message
      };
    }
  }

  return {
    valid: true,
    bank: bankInfo.bank,
    accountType: bankInfo.accountType,
    comment: bankInfo.comment,
    message: bankInfo.comment ? `${bankInfo.bank} (${bankInfo.comment})` : bankInfo.bank
  };
}

/**
 * Validate account number format and check digit
 */
export function validateAccountNumber(clearingNumber, accountNumber) {
  if (!accountNumber) {
    return { valid: false, message: 'Kontonummer är obligatoriskt' };
  }

  const normalizedAccount = normalizeAccountNumber(accountNumber);
  const normalizedClearing = normalizeClearingNumber(clearingNumber);

  // Must be numeric
  if (!/^\d+$/.test(normalizedAccount)) {
    return { valid: false, message: 'Kontonummer får bara innehålla siffror' };
  }

  // Get bank info to validate length
  const bankInfo = getBankInfo(normalizedClearing);

  if (!bankInfo) {
    // If clearing number is unknown, do basic validation
    if (normalizedAccount.length < 6 || normalizedAccount.length > 10) {
      return { valid: false, message: 'Kontonummer måste vara 6-10 siffror' };
    }
    return { valid: true, message: 'Format OK (bank okänd)', warning: true };
  }

  // Check length matches bank requirements
  const expectedLengths = Array.isArray(bankInfo.accountLength)
    ? bankInfo.accountLength
    : [bankInfo.accountLength];

  if (!expectedLengths.includes(normalizedAccount.length)) {
    const lengthStr = expectedLengths.join(' eller ');
    return {
      valid: false,
      message: `${bankInfo.bank} kräver ${lengthStr} siffror i kontonumret (du angav ${normalizedAccount.length} siffror)`
    };
  }

  // Validate check digit based on account type and algorithm
  if (bankInfo.checkDigit) {
    let checkDigitValid = false;

    if (bankInfo.checkDigit === 'mod10') {
      checkDigitValid = modulo10Check(normalizedAccount);
    } else if (bankInfo.checkDigit === 'mod11-1') {
      // Type 1: mod11 on clearing (digits 2-4) + account
      const clearingBase = normalizedClearing.substring(0, 4);
      checkDigitValid = modulo11Type1Check(clearingBase, normalizedAccount);
    } else if (bankInfo.checkDigit === 'mod11-2') {
      // Type 2: mod11 on account only (Handelsbanken, etc.)
      checkDigitValid = modulo11Type2Check(normalizedAccount);
    }

    if (!checkDigitValid) {
      return {
        valid: false,
        message: 'Kontrollsiffra stämmer inte. Kontrollera kontonumret.'
      };
    }
  }

  return {
    valid: true,
    message: 'Giltigt kontonummer'
  };
}

/**
 * Validate complete bank account (clearing + account)
 */
export function validateBankAccount(clearingNumber, accountNumber) {
  const clearingValidation = validateClearingNumber(clearingNumber);
  if (!clearingValidation.valid) {
    return clearingValidation;
  }

  const accountValidation = validateAccountNumber(clearingNumber, accountNumber);
  if (!accountValidation.valid) {
    return accountValidation;
  }

  return {
    valid: true,
    bank: clearingValidation.bank,
    accountType: clearingValidation.accountType,
    message: `Giltigt ${clearingValidation.bank} konto`,
    warning: clearingValidation.warning || accountValidation.warning
  };
}

/**
 * Format bank account for display
 */
export function formatBankAccount(clearingNumber, accountNumber) {
  const clearing = normalizeClearingNumber(clearingNumber);
  const account = normalizeAccountNumber(accountNumber);

  if (!clearing || !account) return '';

  return `${clearing}-${account}`;
}

/**
 * Format clearing number as user types (auto-format)
 */
export function formatClearingOnInput(value) {
  if (!value) return '';
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.substring(0, 5); // Max 5 digits
}

/**
 * Format account number as user types
 */
export function formatAccountOnInput(value) {
  if (!value) return '';
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.substring(0, 10); // Max 10 digits
}
